import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  FormControl,
  FormField,
  FormItem,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@crossnokaye/ux-common';

import { formatDate } from '@/ux-common/components/calendar';
import { CalendarPicker } from '@/ux-common/components/calendar-picker/calendar-picker';
import { DropdownButton } from '@/ux-common/components/dropdown-button/dropdown-button';

import { DefrostScheduleBaseCellProps } from './defrost-schedule.types';
import { DefrostTableCell } from './defrost-table-cell';

const toStartOfDay = (date: Date): Date => {
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return day;
};

export const DefrostScheduleDateCell: React.FC<DefrostScheduleBaseCellProps> = ({ index }) => {
  const { control, setValue } = useFormContext();
  const fieldName = `defrostSchedule.${index}.endDate` as const;
  const todayStartOfDay = useMemo(() => toStartOfDay(new Date()), []);
  const [open, setOpen] = useState(false);

  return (
    <DefrostTableCell>
      {(disabledByPermissions) => (
        <FormField
          control={control}
          name={fieldName}
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormControl>
                <Popover open={open} onOpenChange={setOpen}>
                  <PopoverTrigger asChild>
                    <DropdownButton
                      data-testid="defrost-schedule-end-date-picker"
                      disabled={disabledByPermissions}
                      className="btn-tier3 w-full justify-start"
                    >
                      {field.value
                        ? formatDate(field.value, { format: 'medium' })
                        : 'Repeat Forever'}
                    </DropdownButton>
                  </PopoverTrigger>
                  <PopoverContent className="z-overlay w-auto p-0" withPortal>
                    <CalendarPicker
                      mode="single"
                      defaultValue={field.value ?? undefined}
                      onConfirm={(selectedDate: Date) => {
                        setValue(fieldName, selectedDate ?? null, {
                          shouldDirty: true,
                          shouldTouch: true,
                          shouldValidate: true,
                        });
                        setOpen(false);
                      }}
                      onCancel={() => setOpen(false)}
                      disabled={(date: Date) => toStartOfDay(date) <= todayStartOfDay}
                    />
                  </PopoverContent>
                </Popover>
              </FormControl>
            </FormItem>
          )}
        />
      )}
    </DefrostTableCell>
  );
};
